import { useState, useEffect, useCallback } from 'react';
import { User } from '../services/authService';
import { Book, BookStatus } from '../types/Book';
import { bookService } from '../services/bookService';

export function useBooks(user: User | null) {
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load books from Firebase
  const loadBooks = useCallback(async () => {
    if (!user) {
      console.log('useBooks: No user, skipping load');
      return;
    }
    
    try {
      console.log('useBooks: Loading books for user:', user.id);
      setLoading(true);
      setError(null);
      
      const fetchedBooks = await bookService.getBooks();
      console.log('useBooks: Fetched books:', fetchedBooks);
      setBooks(fetchedBooks);
    } catch (err: any) {
      console.error('useBooks: Error loading books:', err);
      console.error('useBooks: Error details:', {
        code: err.code,
        message: err.message,
        details: err.details
      });
      
      // More specific error handling
      if (err.code === 'unauthenticated') {
        setError('Authentication error. Please sign in again.');
      } else if (err.code === 'permission-denied') {
        setError('Permission denied. Please check your account.');
      } else if (err.message?.includes('network')) {
        setError('Network error. Please check your connection.');
      } else {
        setError(`Failed to load books: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Add a new book
  const addBook = useCallback(async (book: Omit<Book, 'id'>) => {
    try {
      setError(null);
      const newBook = await bookService.addBook(book);
      if (newBook) {
        setBooks(prev => [...prev, newBook]);
        return newBook;
      } else {
        setError('Failed to add book');
        return null;
      }
    } catch (err) {
      setError('Failed to add book');
      console.error('Error adding book:', err);
      return null;
    }
  }, []);

  // Update a book
  const updateBook = useCallback(async (book: Book) => {
    try {
      setError(null);
      const success = await bookService.updateBook(book);
      if (success) {
        setBooks(prev => prev.map(b => b.id === book.id ? book : b));
        return true;
      } else {
        setError('Failed to update book');
        return false;
      }
    } catch (err) {
      setError('Failed to update book');
      console.error('Error updating book:', err);
      return false;
    }
  }, []);

  // Delete a book
  const deleteBook = useCallback(async (bookId: string) => {
    try {
      setError(null);
      const success = await bookService.deleteBook(bookId);
      if (success) {
        setBooks(prev => prev.filter(b => b.id !== bookId));
        return true;
      } else {
        setError('Failed to delete book');
        return false;
      }
    } catch (err) {
      setError('Failed to delete book');
      console.error('Error deleting book:', err);
      return false;
    }
  }, []);

  // Search books
  const searchBooks = useCallback(async (query: string) => {
    try {
      setError(null);
      const results = await bookService.searchBooks(query);
      return results;
    } catch (err) {
      setError('Failed to search books');
      console.error('Error searching books:', err);
      return [];
    }
  }, []);

  // Search books with filters and update the books state (without global loading)
  const searchBooksWithFilters = useCallback(async (query: string, filters: {
    status?: BookStatus;
    author?: string;
    subject?: string;
    language?: string;
    publication_year?: string;
    minRating?: number;
  }): Promise<{ books: Book[]; error: string | null }> => {
    if (!user) {
      console.log('useBooks: No user, skipping search');
      return { books: [], error: 'No user authenticated' };
    }
    
    try {
      console.log('useBooks: Searching books with filters:', { query, filters });
      // Don't set global loading state for search operations
      setError(null);
      
      const searchResults = await bookService.searchBooksWithFilters(query, filters);
      console.log('useBooks: Search results:', searchResults);
      setBooks(searchResults);
      return { books: searchResults, error: null };
    } catch (err: any) {
      console.error('useBooks: Error searching books with filters:', err);
      const errorMessage = `Failed to search books: ${err.message || 'Unknown error'}`;
      setError(errorMessage);
      return { books: [], error: errorMessage };
    }
  }, [user]);

  // Load books when user changes
  useEffect(() => {
    if (user) {
      loadBooks();
    } else {
      // Clear books when user signs out
      setBooks([]);
      setLoading(false);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // loadBooks intentionally excluded to prevent infinite loop

  // Debug: Log books state changes (limited to prevent console overflow)
  useEffect(() => {
    console.log('useBooks: Books state changed:', books.length, 'books');
    if (books.length > 0 && books.length <= 5) {
      console.log('Sample books:', books);
    }
  }, [books]);

  return {
    books,
    loading,
    error,
    addBook,
    updateBook,
    deleteBook,
    searchBooks,
    searchBooksWithFilters,
    refreshBooks: loadBooks
  };
} 